import { useMemo, useState } from 'react';
import {
  Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text,
} from '@tremor/react';
import { t } from '../lib/i18n';
import { usePagination } from '../lib/usePagination';
import Pagination from './Pagination';
import type { InventoryLineRow } from '../api/types';

const fmtMxn = new Intl.NumberFormat('es-MX', {
  style: 'currency',
  currency: 'MXN',
  minimumFractionDigits: 2,
});

const fmtPct = new Intl.NumberFormat('es-MX', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

interface Props {
  rows: InventoryLineRow[];
  showCost: boolean;
  /** line code → description (from /api/part-filters); v2.57. */
  lineDesc?: Record<string, string>;
  /** Click a row to open the line drill-down (v2.57). */
  onRowClick?: (line: string, sid: number) => void;
}

type SortKey = 'gmroi' | 'inv_cost' | 'sku_count' | 'dead_count';

export default function InventoryLineTable({ rows, showCost, lineDesc, onRowClick }: Props) {
  const [sortBy, setSortBy] = useState<SortKey>('gmroi');

  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      switch (sortBy) {
        case 'gmroi':      return (b.gmroi ?? -Infinity) - (a.gmroi ?? -Infinity);
        case 'inv_cost':   return (b.inv_cost_mxn ?? 0) - (a.inv_cost_mxn ?? 0);
        case 'sku_count':  return b.sku_count - a.sku_count;
        case 'dead_count': return b.dead_count - a.dead_count;
      }
    });
    return arr;
  }, [rows, sortBy]);

  const pg = usePagination(sorted);

  if (rows.length === 0) {
    return <Card><Text>{t.inventory.empty}</Text></Card>;
  }

  const headerBtn = (label: string, key: SortKey) => (
    <button
      type="button"
      onClick={() => setSortBy(key)}
      className={'hover:underline ' + (sortBy === key ? 'text-tremor-brand font-semibold' : '')}
    >
      {label}
    </button>
  );

  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t.inventory.cols.line}</TableHeaderCell>
            <TableHeaderCell>{t.inventory.cols.pdesc}</TableHeaderCell>
            <TableHeaderCell>{t.inventory.cols.sid}</TableHeaderCell>
            {showCost && (
              <TableHeaderCell className="text-right">{headerBtn(t.inventory.cols.invCost, 'inv_cost')}</TableHeaderCell>
            )}
            {showCost && (
              <TableHeaderCell className="text-right">{t.inventory.cols.margin}</TableHeaderCell>
            )}
            {showCost && (
              <TableHeaderCell className="text-right">{headerBtn(t.inventory.cols.gmroi, 'gmroi')}</TableHeaderCell>
            )}
            <TableHeaderCell className="text-right">{headerBtn(t.inventory.cols.sku, 'sku_count')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.inventory.cols.dead, 'dead_count')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{t.inventory.cols.low}</TableHeaderCell>
            {showCost && (
              <TableHeaderCell>{t.inventory.cols.abcCounts}</TableHeaderCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {pg.pageRows.map((r) => (
            <TableRow
              key={`${r.line}-${r.sid}`}
              onClick={onRowClick ? () => onRowClick(r.line, r.sid) : undefined}
              className={onRowClick ? 'cursor-pointer hover:bg-tremor-background-muted dark:hover:bg-dark-tremor-background-muted' : ''}
            >
              <TableCell className={'font-mono' + (onRowClick ? ' text-tremor-brand' : '')}>{r.line}</TableCell>
              <TableCell className="text-xs">{lineDesc?.[r.line] ?? '—'}</TableCell>
              <TableCell>{r.sid}</TableCell>
              {showCost && (
                <TableCell className="text-right">{r.inv_cost_mxn !== undefined ? fmtMxn.format(r.inv_cost_mxn) : '—'}</TableCell>
              )}
              {showCost && (
                <TableCell className="text-right">{r.margin90_mxn !== undefined ? fmtMxn.format(r.margin90_mxn) : '—'}</TableCell>
              )}
              {showCost && (
                <TableCell className="text-right">{r.gmroi == null ? '—' : fmtPct.format(r.gmroi)}</TableCell>
              )}
              <TableCell className="text-right">{r.sku_count}</TableCell>
              <TableCell className="text-right">{r.dead_count > 0 ? <span className="text-red-600 dark:text-red-400">{r.dead_count}</span> : r.dead_count}</TableCell>
              <TableCell className="text-right">{r.low_count > 0 ? <span className="text-amber-600 dark:text-amber-400">{r.low_count}</span> : r.low_count}</TableCell>
              {showCost && (
                <TableCell className="text-xs">
                  {r.abc_a_count ?? 0} / {r.abc_b_count ?? 0} / {r.abc_c_count ?? 0}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination {...pg} className="px-4" />
    </Card>
  );
}
